import type {StateCreator} from 'zustand';
import {List, Map} from 'immutable';
import type {AssignmentModelV3, DraftAssignmentModelV1} from '@albert-io/models';
import sessionStore from 'client/Session/SessionStore';

import {getAssignmentQuery} from 'client/components/ContentDiscovery/AssignmentSettings/AssignmentSettings.queries';
import type {ContentDiscoveryStoreState} from 'client/components/ContentDiscovery/store/ContentDiscoveryStore.types';

import type {SelectedRecipients} from '../../Document/Settings/RecipientSettings/RecipientSettings.types';

type AssignmentState = {
  assignment: AssignmentModelV3 | DraftAssignmentModelV1 | null;
  assignmentLoading: boolean;
  latestRequestId: number;
  assignmentResults: List<AssignmentModelV3 | DraftAssignmentModelV1>;
  selectedRecipients: SelectedRecipients; // recipients to display count on confirmation/share list
  isReassign: boolean;
  classrooms: List<any>;
  isDirty?: boolean;
  isNavigatingAway?: boolean;
};

type AssignmentActions = {
  getAssignmentById: (
    assignmentId: string,
    isNowADraft: boolean | undefined,
    signal?: AbortSignal
  ) => void;
  setAssignmentResults: (assignmentResults: List<AssignmentModelV3>) => void;
  setSelectedRecipients: (selectedRecipients: SelectedRecipients) => void;
  reset: () => void;
  setIsReassign: (isReassign: boolean) => void;
  setClassrooms: (classrooms: List<any>) => void;
  setIsDirty: (isDirty: boolean) => void;
  setIsNavigatingAway: (isNavigatingAway: boolean) => void;
};

const initialState: AssignmentState = {
  assignment: null,
  assignmentLoading: false,
  latestRequestId: 0,
  assignmentResults: List(),
  selectedRecipients: Map(),
  isReassign: false,
  classrooms: List(),
  isDirty: false,
  isNavigatingAway: false
};

export type AssignmentSlice = AssignmentState & AssignmentActions;

export const createAssignmentSlice: StateCreator<
  ContentDiscoveryStoreState,
  [['zustand/immer', never]],
  [],
  AssignmentSlice
> = (set, get): AssignmentSlice => ({
  ...initialState,
  // initial fetch of subjects by curriculum areas along with personalized and classroom subjects
  getAssignmentById: async (assignmentId, isNowADraft, signal) => {
    if (!assignmentId) {
      return;
    }

    const requestId = get().assignment.latestRequestId + 1;
    set((state) => {
      state.assignment.assignmentLoading = true;
      state.assignment.latestRequestId = requestId;
    });
    try {
      const isDraft = isNowADraft ?? get().draft.isDraft;
      const [fetchedAssignment] = await Promise.all([
        getAssignmentQuery(assignmentId, sessionStore.isSchoolAdministrator(), isDraft, signal)
      ]);
      // A newer request started while this one was in flight; drop the stale result.
      if (get().assignment.latestRequestId !== requestId) {
        return;
      }
      set((state) => {
        state.assignment.assignmentLoading = false;
        state.assignment.assignment = fetchedAssignment;
      });
    } catch (error) {
      // Aborted (builder unmounted or superseded by a newer fetch) — not a load failure;
      // don't clear state or rethrow, which would surface a stale error toast.
      if (signal?.aborted) {
        return;
      }
      if (get().assignment.latestRequestId !== requestId) {
        throw error;
      }
      set((state) => {
        state.assignment.assignmentLoading = false;
        state.assignment.assignment = null;
      });
      throw error;
    }
  },

  setAssignmentResults: (assignmentResults) =>
    set((state) => {
      state.assignment.assignmentResults = assignmentResults;
    }),

  setSelectedRecipients: (selectedRecipients) =>
    set((state) => {
      state.assignment.selectedRecipients = selectedRecipients;
    }),

  setIsReassign: (isReassign = true) =>
    set((state) => {
      state.assignment.isReassign = isReassign;
    }),

  setClassrooms: (classrooms) =>
    set((state) => {
      state.assignment.classrooms = classrooms;
    }),

  setIsDirty: (isDirty) =>
    set((state) => {
      state.assignment.isDirty = isDirty;
    }),

  setIsNavigatingAway: (isNavigatingAway) =>
    set((state) => {
      state.assignment.isNavigatingAway = isNavigatingAway;
    }),

  reset: () =>
    set((state) => {
      state.assignment = {...state.assignment, ...initialState};
    })
});
